import type {
  CardinalForms,
  CopyContextValue,
  CopyKey,
  OrdinalForms,
  PluralCopy,
  PluralVariable,
  SupportedLocales,
  Variables,
} from './types'

export class FatalCopyError extends Error {
  constructor(
    key: CopyKey,
    locale: SupportedLocales,
    fallback: string | CardinalForms | OrdinalForms,
    variables?: Variables<string> | PluralVariable,
    original?: ErrorOptions['cause'],
  ) {
    super(`FatalCopyError: Unable to resolve any copy for key='${key}'`, {
      cause: {
        key,
        locale,
        fallbackCopy: fallback,
        variables,
        originalError: original,
      },
    })
  }
}

export class MissingCopyBundleError extends Error {
  constructor(context: CopyContextValue, original?: ErrorOptions['cause']) {
    super(
      `MissingCopyBundleError: Expected to find a copy bundle from context, but it is ${context.copy}.`,
      {
        cause: {
          ...context,
          originalError: original,
        },
      },
    )
  }
}

export class CopyNotFoundError extends Error {
  constructor(
    key: CopyKey,
    locale?: SupportedLocales,
    original?: ErrorOptions['cause'],
  ) {
    super(
      `CopyNotFoundError: Copy with key='${key}' not found ${
        locale ? `for locale ${locale}` : ''
      }. Attempting to use fallback instead.`,
      {
        cause: {
          originalError: original,
        },
      },
    )
  }
}

export class UnrecognisedCopyError extends Error {
  constructor(
    key: CopyKey,
    locale: SupportedLocales,
    copy: string,
    variables?: Variables<string> | PluralVariable,
    original?: ErrorOptions['cause'],
  ) {
    super(
      `UnrecognisedCopyError: Found copy with value '${copy}', but this is not a recognised format.`,
      {
        cause: {
          key,
          locale,
          variables,
          originalError: original,
        },
      },
    )
  }
}

export class BadPluralError extends Error {
  constructor(
    key: CopyKey,
    copy: PluralCopy | CardinalForms | OrdinalForms,
    variables?: Variables<string> | PluralVariable,
    original?: ErrorOptions['cause'],
  ) {
    super(
      `BadPluralError: A plural form can only be selected using one parameter, but for copy key='${key}' we received ${
        Object.keys(variables ?? {}).length
      }.`,
      {
        cause: {
          key,
          copy,
          variables,
          originalError: original,
        },
      },
    )
  }
}

export class VariableSubstitutionError extends Error {
  constructor(
    copy: string,
    variables: Variables<string>,
    original?: ErrorOptions['cause'],
  ) {
    super(
      `VariableSubstitutionError: Could not create a copy string using template='${copy}' and variables=${JSON.stringify(
        variables,
      )}.`,
      {
        cause: {
          copy,
          variables,
          originalError: original,
        },
      },
    )
  }
}
